(() => {
  const bindOnce = (el, key) => {
    if (!el || el.dataset[key] === 'true') return false;
    el.dataset[key] = 'true';
    return true;
  };

  const initHero = (root) => {
    const hero = root.matches?.('[data-minus-shop-hero]') ? root : root.querySelector('[data-minus-shop-hero]');
    if (!hero || !bindOnce(hero, 'minusShopBound')) return;

    const slidesContainer = hero.querySelector('[data-shop-slides]');
    const thumbnails = Array.from(hero.querySelectorAll('[data-shop-thumb]'));
    const dots = Array.from(hero.querySelectorAll('[data-shop-dot]'));
    const prevBtn = hero.querySelector('[data-shop-prev]');
    const nextBtn = hero.querySelector('[data-shop-next]');
    if (!slidesContainer) return;

    const slides = Array.from(slidesContainer.querySelectorAll('.shop-hero-slide'));
    const totalSlides = slides.length || dots.length || thumbnails.length;
    if (totalSlides <= 1) return;

    let currentIndex = 0;

    const getIndexFromScroll = () => {
      const width = slidesContainer.clientWidth || 1;
      const rawIndex = Math.round(slidesContainer.scrollLeft / width);
      return Math.max(0, Math.min(rawIndex, totalSlides - 1));
    };

    const sync = (index) => {
      const activeIdx = typeof index === 'number' ? index : getIndexFromScroll();
      dots.forEach((dot, idx) => dot.classList.toggle('active', idx === activeIdx));
      thumbnails.forEach((thumb, idx) => {
        const isActive = idx === activeIdx;
        thumb.classList.toggle('active', isActive);
        if (isActive) {
          const container = thumb.parentElement;
          if (container && container.classList.contains('shop-hero-thumbnails')) {
            const top = thumb.offsetTop - container.offsetTop;
            if (top < container.scrollTop || top + thumb.offsetHeight > container.scrollTop + container.clientHeight) {
              thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }
        }
      });
    };

    const goTo = (index) => {
      if (totalSlides <= 0) return;
      currentIndex = ((index % totalSlides) + totalSlides) % totalSlides;
      const targetSlide = slides[currentIndex];
      const targetLeft = targetSlide ? targetSlide.offsetLeft : currentIndex * slidesContainer.clientWidth;
      slidesContainer.scrollTo({
        left: targetLeft,
        behavior: 'smooth',
      });
      sync(currentIndex);
    };

    slidesContainer.addEventListener('scroll', () => {
      currentIndex = getIndexFromScroll();
      sync(currentIndex);
    }, { passive: true });

    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        goTo(index);
        resetAutoplay();
      });
    });

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        goTo(index);
        resetAutoplay();
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        goTo(currentIndex - 1);
        resetAutoplay();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        goTo(currentIndex + 1);
        resetAutoplay();
      });
    }

    // Autoplay implementation
    const isAutoplay = hero.dataset.autoplay === 'true';
    const autoplaySpeed = parseInt(hero.dataset.autoplaySpeed, 10) || 5000;
    let autoplayTimer = null;
    let isHovered = false;
    let isFocused = false;
    let isTouching = false;

    const shouldAutoplay = () => {
      if (!isAutoplay || totalSlides <= 1) return false;
      if (isHovered || isFocused || isTouching) return false;
      if (document.hidden) return false;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      return true;
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!shouldAutoplay()) return;
      autoplayTimer = setInterval(() => {
        if (!shouldAutoplay()) return;
        goTo(currentIndex + 1);
      }, autoplaySpeed);
    };

    const stopAutoplay = () => {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const resetAutoplay = () => {
      stopAutoplay();
      startAutoplay();
    };

    const gallery = hero.querySelector('.shop-hero-gallery') || hero;

    gallery.addEventListener('mouseenter', () => {
      isHovered = true;
      stopAutoplay();
    });

    gallery.addEventListener('mouseleave', () => {
      isHovered = false;
      startAutoplay();
    });

    gallery.addEventListener('focusin', () => {
      isFocused = true;
      stopAutoplay();
    });

    gallery.addEventListener('focusout', () => {
      isFocused = false;
      startAutoplay();
    });

    slidesContainer.addEventListener('touchstart', () => {
      isTouching = true;
      stopAutoplay();
    }, { passive: true });

    slidesContainer.addEventListener('touchend', () => {
      isTouching = false;
      resetAutoplay();
    }, { passive: true });

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    hero._cleanupAutoplay = () => {
      stopAutoplay();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };

    startAutoplay();
  };

  const initAccordion = (root) => {
    const wrap = root.matches?.('[data-minus-shop-how]') ? root : root.querySelector('[data-minus-shop-how]');
    if (!wrap || !bindOnce(wrap, 'minusShopHowBound')) return;

    // Set initial maxHeight for the active accordion-body on page load
    const activeBody = wrap.querySelector('.accordion-item.active .accordion-body');
    if (activeBody) {
      activeBody.style.maxHeight = activeBody.scrollHeight + 'px';
    }

    wrap.querySelectorAll('[data-accordion-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const item = button.closest('.accordion-item');
        if (!item) return;
        const body = item.querySelector('.accordion-body');
        const open = item.classList.contains('active');

        // Close all items
        wrap.querySelectorAll('.accordion-item').forEach((el) => {
          el.classList.remove('active');
          const elBody = el.querySelector('.accordion-body');
          if (elBody) {
            if (elBody.style.maxHeight === 'none') {
              elBody.style.maxHeight = elBody.scrollHeight + 'px';
              elBody.offsetHeight; // Force reflow
            }
            elBody.style.maxHeight = '0px';
          }
        });

        // Toggle current item
        if (!open) {
          item.classList.add('active');
          if (body) {
            body.style.maxHeight = body.scrollHeight + 'px';
            
            const onTransitionEnd = (e) => {
              if (e.propertyName === 'max-height' && item.classList.contains('active')) {
                body.style.maxHeight = 'none';
                body.removeEventListener('transitionend', onTransitionEnd);
              }
            };
            body.addEventListener('transitionend', onTransitionEnd);
          }
        }
      });
    });
  };

  const initJourneyPill = (root) => {
    const wrap = root.matches?.('[data-journey-pill-wrap]')
      ? root
      : root.querySelector?.('[data-journey-pill-wrap]');
    if (!wrap || !bindOnce(wrap, 'minusJourneyPillBound')) return;

    const btn = wrap.querySelector('[data-journey-pill-btn]');
    if (!btn) return;

    let isSticky = false;

    const updateSticky = () => {
      const rect = wrap.getBoundingClientRect();
      const shouldStick = rect.top <= 10;

      if (shouldStick !== isSticky) {
        isSticky = shouldStick;
        btn.classList.toggle('is-sticky', isSticky);
        wrap.classList.toggle('is-sticky', isSticky);
      }
    };

    window.addEventListener('scroll', updateSticky, { passive: true, capture: true });
    document.addEventListener('scroll', updateSticky, { passive: true, capture: true });
    window.addEventListener('touchmove', updateSticky, { passive: true, capture: true });
    window.addEventListener('resize', updateSticky, { passive: true });

    let observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(() => {
        updateSticky();
      }, { threshold: [0, 0.1, 0.5, 1] });
      observer.observe(wrap);
    }

    updateSticky();

    wrap._cleanupSticky = () => {
      window.removeEventListener('scroll', updateSticky);
      document.removeEventListener('scroll', updateSticky);
      window.removeEventListener('touchmove', updateSticky);
      window.removeEventListener('resize', updateSticky);
      if (observer) observer.disconnect();
    };
  };

  const initAll = (root = document) => {
    initHero(root);
    initAccordion(root);
    initJourneyPill(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const hero = event.target?.matches?.('[data-minus-shop-hero]')
      ? event.target
      : event.target?.querySelector?.('[data-minus-shop-hero]');
    if (hero && typeof hero._cleanupAutoplay === 'function') {
      hero._cleanupAutoplay();
    }
    const pillWrap = event.target?.matches?.('[data-journey-pill-wrap]')
      ? event.target
      : event.target?.querySelector?.('[data-journey-pill-wrap]');
    if (pillWrap && typeof pillWrap._cleanupSticky === 'function') {
      pillWrap._cleanupSticky();
    }
  });
})();
